/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Alert from 'flarum/common/components/Alert';
import ArticleListState from 'flarum/orell-articles/states/ArticleListState';
import ArticleListItem from 'flarum/orell-articles/components/ArticleListItem';
import ArticleSearchSource from 'flarum/orell-articles/components/ArticleSearchSource';
import ArticlePickerModal from 'flarum/orell-articles/components/ArticlePickerModal';
import ArticleRating from '../components/ArticleRating';
import newReviewAction from '../utils/newReviewAction';
import type Article from 'flarum/orell-articles/models/Article';

export default function () {
	const articlesSortFunc = (a: Article, b: Article) => {
		if (a.allReviewsCount() && !b.allReviewsCount()) {
			return -1;
		}
		if (b.allReviewsCount() && !a.allReviewsCount()) {
			return 1;
		}
		return 0;
	};

	extend(ArticleListState.prototype, 'manipulateResults', function () {
		// `articles` is `false` when the query returned nothing at all.
		if (!this.isFirstPage || !this.articles) {
			return;
		}

		this.articles.sort(articlesSortFunc);

		// Redundant while `handleSearchSuccess()` also redraws, but rAF-coalesced and keeps the
		// ordering independent of that.
		m.redraw();
	});

	extend(ArticleSearchSource.prototype, 'manipulateResults', function (results) {
		// In place, because `extend()` discards return values and the search source renders the
		// very array passed in here.
		results.sort(articlesSortFunc);
	});

	extend(ArticleListItem.prototype, 'infoItems', function (items) {
		const article = this.attrs.article;
		if (article.allReviewsCount()) {
			// The home page leaderboard loads `recentRatingAvg` and wants that shown instead.
			let rating = article.recentRatingAvg() ?? article.rating();

			items.add('rating', <ArticleRating value={rating} />);
		}
	});

	extend(ArticlePickerModal.prototype, 'onBeforeSave', function () {
		const article = this.articles.length ? this.articles[0] : undefined;
		const ownReview = article?.ownReview();

		if (article && ownReview) {
			if (ownReview.isSimple()) {
				app.modal.close();
				app.composer.close();

				// Reopen with the article and its rating preselected. Without the setTimeout the
				// composer glitches visibly.
				setTimeout(() => newReviewAction(article));
				return;
			}

			// Already reviewed with text: send the user to that review instead.
			app.modal.close();
			app.composer.close();
			app.alerts.show(
				Alert,
				{
					type: 'error',
				},
				app.translator.trans('glowingblue-orell-rezensionen.forum.composer_review.already_reviewed_message')
			);
			const reviewDiscussion = ownReview.discussion();

			if (reviewDiscussion) {
				m.route.set(app.route.discussion(reviewDiscussion));
			}
		}
	});
}
